/* =============================================================================
   assets/rm-product.js

   Two self-contained IIFEs:
     1. Variant switcher  — handles option selection, price update, URL history,
                            and Combined Listing cross-product navigation.
     2. Lightbox          — full-screen image/video viewer for the gallery.
   ============================================================================= */

/* =============================================================================
   PART 1 — VARIANT SWITCHER
   ============================================================================= */
(function () {
  'use strict';

  /* ── Bootstrap ── */
  const jsonEl = document.getElementById('rm-product-json');
  if (!jsonEl) {
    console.warn('[RM Product] #rm-product-json not found — variant switching disabled');
    return;
  }
  console.log('[RM Product] Initializing variant switcher');

  let productData;
  try {
    productData = JSON.parse(jsonEl.textContent);
  } catch (e) {
    console.error('[RM] Could not parse product JSON', e);
    return;
  }

  const variants   = productData.variants;
  const addBtn     = document.querySelector('input.rm-band__btn-add-to-cart');

  /* ── Currency formatter ── */
  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  /* ── Read currently selected option values from the DOM ── */
  function getSelectedOptions() {
    const opts = [];
    document.querySelectorAll('.rm-variant-group').forEach(group => {
      const index   = parseInt(group.dataset.optionIndex, 10);
      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked) {
        opts[index] = checked.value;
        return;
      }
      const select = group.querySelector('select.rm-variant-select');
      if (select) opts[index] = select.value;
    });
    return opts;
  }

  /* ── Find the variant that matches all selected options ── */
  function findVariant(selectedOpts) {
    return variants.find(v => v.options.every((val, i) => val === selectedOpts[i])) || null;
  }

  /* ── Update the label values shown next to each option heading ── */
  function updateOptionLabels(selectedOpts) {
    document.querySelectorAll('.rm-variant-group').forEach(group => {
      const index   = parseInt(group.dataset.optionIndex, 10);
      const labelEl = group.querySelector('.rm-variant-label__value');
      if (labelEl && selectedOpts[index] !== undefined) {
        labelEl.textContent = selectedOpts[index];
      }
    });
  }

  /* ── Sync is-active class on swatch chips ── */
  function syncSwatchActiveStates() {
    document.querySelectorAll('.rm-variant-group').forEach(group => {
      group.querySelectorAll('.rm-swatch').forEach(swatch => {
        const radio = swatch.querySelector('input[type="radio"]');
        if (radio) swatch.classList.toggle('is-active', radio.checked);
      });
    });
  }

  /* ── Update price display ── */
  function updatePrice(variant) {
    const priceEl = document.querySelector('.rm-band__price-row .rm-price');
    if (!priceEl) return;

    if (!variant) {
      priceEl.innerHTML = '<span class="rm-price__regular">—</span>';
      return;
    }

    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    priceEl.classList.toggle('rm-price--on-sale', onSale);

    if (onSale) {
      priceEl.innerHTML = `
        <span class="rm-price__sale">${formatMoney(variant.price)}</span>
        <s class="rm-price__compare">${formatMoney(variant.compare_at_price)}</s>`;
    } else {
      priceEl.innerHTML = `<span class="rm-price__regular">${formatMoney(variant.price)}</span>`;
    }

    // Shop Pay installments form
    const installmentInput = document.querySelector('#product-form-installment-band input[name="id"]');
    if (installmentInput) installmentInput.value = variant.id;
  }

  /* ── Update add-to-cart button availability ── */
  function updateButton(variant) {
    const btn = document.querySelector('input.rm-band__btn-add-to-cart');
    if (!btn) return;
    if (!variant || !variant.available) {
      btn.disabled = true;
      btn.value    = variant ? 'Sold Out' : 'Unavailable';
    } else {
      btn.disabled = false;
      btn.value    = btn.dataset.originalValue || 'Order this Ring';
    }
  }

  /* ── Show/hide Order This Ring based on availability ── */
  function updateOrderCta(variant) {
    const cta      = document.querySelector('.rm-band__order-cta');
    const badge    = document.getElementById('rm-sold-out-badge');
    const soldBtn  = document.getElementById('rm-sold-out-atc');
    const soldOut  = !variant || !variant.available;
    if (cta)     cta.style.display     = soldOut ? 'none' : '';
    if (badge)   badge.style.display   = soldOut ? 'flex' : 'none';
    if (soldBtn) soldBtn.style.display = soldOut ? '' : 'none';
  }

  /* ── Sync hidden variant ID inputs (all forms on the page) ── */
  function updateVariantInputs(variant) {
    if (!variant) return;
    document.querySelectorAll('input.formVariantId').forEach(inp => {
      inp.value = variant.id;
    });
  }

  /* ── Update the URL ?variant= param without a page reload ── */
  function updateUrl(variant) {
    if (!variant || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({ variantId: variant.id }, '', url.toString());
  }

  /* ── Main update — called on every option change ── */
  function onVariantChange() {
    const selectedOpts = getSelectedOptions();
    const variant      = findVariant(selectedOpts);

    console.log('[RM Product] Options:', selectedOpts, '| Variant:', variant?.id, variant?.price);

    updateOptionLabels(selectedOpts);
    syncSwatchActiveStates();
    updatePrice(variant);
    updateButton(variant);
    updateOrderCta(variant);
    updateVariantInputs(variant);
    updateUrl(variant);

    document.dispatchEvent(new CustomEvent('rm:variantChange', {
      bubbles: true,
      detail: { variant, product: productData, selectedOptions: selectedOpts },
    }));
  }

  /* ── Attach change listeners ──
     Combined Listing guard: if the changed input's data-product-url points
     to a different product, navigate there instead of switching variants.
     Compare by product handle (not full path) so collection-context URLs
     (/collections/x/products/handle) don't false-trigger navigation.
  ── */
  function productHandle(path) {
    const m = (path || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : path.replace(/\/$/, '');
  }
  const currentHandle = productHandle(window.location.pathname);

  function attachListeners() {
    document.querySelectorAll('.rm-variant-group input[type="radio"]').forEach(input => {
      input.addEventListener('change', function (e) {
        const targetUrl = e.target.dataset.productUrl;
        const isCrossProduct = targetUrl &&
          productHandle(targetUrl) !== currentHandle;
        if (isCrossProduct) {
          window.location.href = targetUrl;
          return;
        }
        onVariantChange();
      });
    });

    document.querySelectorAll('.rm-variant-group select.rm-variant-select').forEach(select => {
      select.addEventListener('change', function (e) {
        const selectedOption = e.target.options[e.target.selectedIndex];
        const targetUrl      = selectedOption?.dataset.productUrl;
        const isCrossProduct = targetUrl &&
          productHandle(targetUrl) !== currentHandle;
        if (isCrossProduct) {
          window.location.href = targetUrl;
          return;
        }
        onVariantChange();
      });
    });
  }

  /* ── Init ── */
  attachListeners();
  if (addBtn) addBtn.dataset.originalValue = addBtn.value;

  // Single-variant products (no variant groups rendered)
  if (!document.querySelector('.rm-variant-group') && variants.length > 0) {
    updatePrice(variants[0]);
    updateButton(variants[0]);
    updateOrderCta(variants[0]);
    updateVariantInputs(variants[0]);
    return;
  }

  // Pre-select variant from URL ?variant= param, then run initial update
  (function preselectFromUrl() {
    const variantId = new URLSearchParams(window.location.search).get('variant');
    if (variantId) {
      const match = variants.find(v => String(v.id) === variantId);
      if (match) {
        document.querySelectorAll('.rm-variant-group').forEach(group => {
          const index = parseInt(group.dataset.optionIndex, 10);
          const value = match.options[index];
          const radio = group.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
          if (radio) {
            radio.checked = true;
            return;
          }
          const select = group.querySelector('select.rm-variant-select');
          if (select) select.value = value;
        });
      }
    }
    onVariantChange();
  })();

})();


/* =============================================================================
   PART 2 — LIGHTBOX
   ============================================================================= */
(function () {
  'use strict';

  /* DOM refs — created lazily on first open */
  let overlay, imgEl, mediaWrap, closeBtn, prevBtn, nextBtn, counter;
  let mediaList = [];
  let currentIndex = 0;

  const BTN_STYLE = [
    'position:fixed',
    'width:44px',
    'height:44px',
    'background:rgba(255,255,255,0.12)',
    'border:0.5px solid rgba(255,255,255,0.25)',
    'color:#fff',
    'font-size:20px',
    'line-height:1',
    'cursor:pointer',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'font-family:inherit',
    'transition:background 0.15s',
    'z-index:10001',
  ].join(';');

  /* ── Build DOM on first use ── */
  function buildDOM() {
    // Mobile responsive overrides
    const style = document.createElement('style');
    style.textContent = [
      '@media (max-width: 680px) {',
      '  #rm-lightbox-overlay { padding: 0 !important; align-items: center !important; }',
      '  #rm-lightbox-img { max-width: 100vw !important; max-height: 100vh !important; width: 100% !important; box-shadow: none !important; }',
      '  #rm-lightbox-prev { top: 16px !important; left: auto !important; right: 120px !important; transform: none !important; border-radius: 50% !important; width: 36px !important; height: 36px !important; }',
      '  #rm-lightbox-next { top: 16px !important; right: 68px !important; transform: none !important; border-radius: 50% !important; width: 36px !important; height: 36px !important; }',
      '  #rm-lightbox-close { top: 16px !important; right: 16px !important; border-radius: 50% !important; width: 36px !important; height: 36px !important; }',
      '  #rm-lightbox-counter { bottom: 24px !important; }',
      '}',
    ].join('');
    document.head.appendChild(style);

    overlay = document.createElement('div');
    overlay.id = 'rm-lightbox-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Image zoom');
    overlay.style.cssText = [
      'display:none',
      'position:fixed',
      'inset:0',
      'z-index:10000',
      'background:rgba(0,0,0,0.92)',
      'align-items:center',
      'justify-content:center',
      'padding:64px 80px',
    ].join(';');

    imgEl = document.createElement('img');
    imgEl.id = 'rm-lightbox-img';
    imgEl.style.cssText = [
      'max-width:100%',
      'max-height:90vh',
      'object-fit:contain',
      'cursor:default',
      'display:block',
      'box-shadow:0 8px 48px rgba(0,0,0,0.6)',
      'transition:opacity 0.18s',
      'opacity:0',
      'user-select:none',
    ].join(';');

    mediaWrap = document.createElement('div');
    mediaWrap.id = 'rm-lightbox-media';
    mediaWrap.style.cssText = 'display:flex;align-items:center;justify-content:center;max-width:90vw;max-height:85vh;';
    mediaWrap.appendChild(imgEl);

    closeBtn = document.createElement('button');
    closeBtn.id = 'rm-lightbox-close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.style.cssText = BTN_STYLE + ';top:16px;right:16px;font-size:24px;';
    closeBtn.innerHTML = '&times;';

    prevBtn = document.createElement('button');
    prevBtn.id = 'rm-lightbox-prev';
    prevBtn.setAttribute('aria-label', 'Previous image');
    prevBtn.style.cssText = BTN_STYLE + ';top:50%;left:16px;transform:translateY(-50%);';
    prevBtn.innerHTML = '&#8592;';

    nextBtn = document.createElement('button');
    nextBtn.id = 'rm-lightbox-next';
    nextBtn.setAttribute('aria-label', 'Next image');
    nextBtn.style.cssText = BTN_STYLE + ';top:50%;right:16px;transform:translateY(-50%);';
    nextBtn.innerHTML = '&#8594;';

    counter = document.createElement('div');
    counter.id = 'rm-lightbox-counter';
    counter.style.cssText = [
      'position:fixed',
      'bottom:20px',
      'left:50%',
      'transform:translateX(-50%)',
      'color:rgba(255,255,255,0.45)',
      'font-size:12px',
      'letter-spacing:0.12em',
      'font-family:inherit',
      'user-select:none',
    ].join(';');

    overlay.appendChild(mediaWrap);
    overlay.appendChild(closeBtn);
    overlay.appendChild(prevBtn);
    overlay.appendChild(nextBtn);
    overlay.appendChild(counter);
    document.body.appendChild(overlay);

    // Button hover states
    [prevBtn, nextBtn, closeBtn].forEach(btn => {
      btn.addEventListener('mouseenter', function () { this.style.background = 'rgba(255,255,255,0.22)'; });
      btn.addEventListener('mouseleave', function () { this.style.background = 'rgba(255,255,255,0.12)'; });
    });

    overlay.addEventListener('click', e => { if (e.target === overlay) closeLightbox(); });
    closeBtn.addEventListener('click', closeLightbox);
    prevBtn.addEventListener('click', e => { e.stopPropagation(); navigate(-1); });
    nextBtn.addEventListener('click', e => { e.stopPropagation(); navigate(1); });

    document.addEventListener('keydown', e => {
      if (overlay.style.display === 'none') return;
      if (e.key === 'Escape')     closeLightbox();
      if (e.key === 'ArrowLeft')  navigate(-1);
      if (e.key === 'ArrowRight') navigate(1);
    });

    // Touch swipe
    let touchStartX = 0;
    overlay.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
    overlay.addEventListener('touchend',   e => {
      const delta = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(delta) > 40) navigate(delta > 0 ? 1 : -1);
    }, { passive: true });
  }

  /* ── Navigate to adjacent media item ── */
  function navigate(dir) {
    if (mediaList.length <= 1) return;
    currentIndex = (currentIndex + dir + mediaList.length) % mediaList.length;
    showMedia(mediaList[currentIndex]);
    updateCounter();
  }

  /* ── Show a media item (image or video) ── */
  function showMedia(item) {
    const isObj          = item && typeof item === 'object';
    const src            = isObj ? item.src : item;
    const type           = isObj ? (item.type || 'image') : 'image';
    const videoSources   = isObj ? (item.videoSources || []) : [];
    const isVideo        = type === 'video' || type === 'external_video';

    // Remove any existing video
    const existingVideo = mediaWrap.querySelector('video');
    if (existingVideo) { existingVideo.pause(); existingVideo.remove(); }

    if (isVideo && videoSources.length) {
      imgEl.style.display = 'none';
      const video = document.createElement('video');
      video.controls   = true;
      video.autoplay   = true;
      video.playsInline = true;
      video.muted      = false;
      video.style.cssText = 'max-width:90vw;max-height:85vh;display:block;outline:none;box-shadow:0 8px 48px rgba(0,0,0,0.6);';
      videoSources.forEach(url => {
        const source = document.createElement('source');
        source.src  = url;
        source.type = 'video/mp4';
        video.appendChild(source);
      });
      if (src) video.poster = src;
      mediaWrap.appendChild(video);
      video.play().catch(() => {});
    } else {
      imgEl.style.display = 'block';
      imgEl.style.opacity = '0';
      const preload = new Image();
      preload.onload = () => { imgEl.src = src; imgEl.style.opacity = '1'; };
      preload.src = src;
    }
  }

  /* ── Update prev/next/counter visibility ── */
  function updateCounter() {
    const multi = mediaList.length > 1;
    prevBtn.style.display  = multi ? 'flex' : 'none';
    nextBtn.style.display  = multi ? 'flex' : 'none';
    counter.style.display  = multi ? 'block' : 'none';
    if (multi) counter.textContent = `${currentIndex + 1} / ${mediaList.length}`;
  }

  /* ── Public open ── */
  function openLightbox(src, list, startIndex) {
    if (!overlay) buildDOM();

    if (list && list.length) {
      mediaList = list;
      if (startIndex !== undefined) {
        currentIndex = startIndex;
      } else {
        currentIndex = 0;
        for (let i = 0; i < mediaList.length; i++) {
          const itemSrc = mediaList[i] && typeof mediaList[i] === 'object' ? mediaList[i].src : mediaList[i];
          if (itemSrc === src) { currentIndex = i; break; }
        }
      }
    } else {
      // Fall back to gallery thumbnails if no list provided
      const thumbs = document.querySelectorAll('.rm-band__thumb');
      if (thumbs.length > 1) {
        mediaList = Array.prototype.map.call(thumbs, thumb => ({
          src:          thumb.dataset.src,
          type:         thumb.dataset.mediaType || 'image',
          videoSources: thumb.dataset.videoSources
            ? thumb.dataset.videoSources.split('|').filter(u => u && !u.includes('.m3u8'))
            : [],
        }));
        currentIndex = 0;
        for (let i = 0; i < mediaList.length; i++) {
          if (mediaList[i].src === src) { currentIndex = i; break; }
        }
      } else {
        mediaList = [src];
        currentIndex = 0;
      }
    }

    overlay.style.display   = 'flex';
    document.body.style.overflow = 'hidden';
    updateCounter();
    showMedia(mediaList[currentIndex]);
  }

  /* ── Public close ── */
  function closeLightbox() {
    if (!overlay) return;
    const video = mediaWrap?.querySelector('video');
    if (video) { video.pause(); video.remove(); }
    overlay.style.display        = 'none';
    imgEl.src                    = '';
    imgEl.style.display          = 'block';
    document.body.style.overflow = '';
  }

  window.rmLightbox = { open: openLightbox, close: closeLightbox };

})();
